/**
 * Product Vue Mixin
 * ------------------------------------------------------------------------------
 * Product form and common product functionality that is not specific to a view.
 *
 * Mixin is shared with:
 * - product-detail.js
 * - product-item.js
 */

import isEqual from 'lodash-es/isEqual'

import ButtonSpinner from '../components/button-spinner.vue'

import { app } from '../vue/app.js'
import { getProductOptions } from '../utils/data'

export const product = {
  components: {
    ButtonSpinner
  },

  props: {
    product: {
      type: Object,
      required: true
    },

    collectionUrl: {
      type: String,
      default: null
    },

    promotionalPrice: {
      type: Number,
      default: null
    },

    // Liquid only data e.g. filtered variant media
    featuredMedia: {
      type: Object
    },

    // Liquid only data e.g. filtered variant and search analytics queries
    url: {
      type: String
    },
    isWelcomeDw: {
      type: Boolean,
      default: null
    },

    welcomeDwLimit: {
      type: Number,
      default: null
    },

    customerTags: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isSubmitting: false,
      currentVariant: null,
      selectedOptions: [],
      productOptions: [],
      media: null,
      parsedFileUrl: '',
      rawFileUrl: window.rawFileUrl
    }
  },

  computed: {
    cart () {
      return app.cart
    },

    isWelcomeDwComputed () {
      const isExplicit = this.isWelcomeDw !== null
      const hasWelcomeDwTag = this.product?.tags?.some(tag =>
        tag.toLowerCase().includes('welcomedw')
      ) || false
      const result = isExplicit ? this.isWelcomeDw : hasWelcomeDwTag
      return result
    },

    welcomeDwLimitComputed () {
      if (this.welcomeDwLimit !== null) return this.welcomeDwLimit
      return window.theme_settings?.welcomedw_sku_limit_per_customer || 0
    },

    customerTagsComputed () {
      if (this.customerTags !== null) return this.customerTags
      return window.customer_tags || ''
    },

    isSale () {
      return (this.currentVariant?.compare_at_price > this.currentVariant?.price) || (this.product?.compare_at_price > this.product?.price)
    },

    baseUrl () {
      return `${this.collectionUrl || ''}/products/`
    },

    productUrl () {
      return `${this.baseUrl}${this.product.handle}`
    },

    tileHref () {
      return this.url || this.product.url || this.productUrl
    },

    productBadges () {
      const badges = this.product.tags.reduce((filtered, badge) => {
        if (badge.toLowerCase().includes('badge|')) {
          filtered.push(badge.split('|').pop())
        }
        return filtered
      }, [])
      return badges
    },

    promoBar () {
      const promoBar = this.product.tags.find(tag => tag.toLowerCase().includes('promo_bar|'))

      return promoBar ? promoBar.split('|').pop() : null
    },

    productLabel () {
      const productLabels = this.product.tags.filter(tag => tag.toLowerCase().includes('product_label|'))

      const finalProductLabel = productLabels.find(label => !label.toLowerCase().includes('cart_product_label|'))

      return finalProductLabel ? finalProductLabel.split('|').pop() : null
    },

    bottlesPerCase () {
      const bottlesPerCase = this.product.tags.find(tag => tag.toLowerCase().includes('bottles_per_case|'))

      return bottlesPerCase ? parseInt(bottlesPerCase.split('|').pop()) : null
    },

    bottlesPerPack () {
      const bottlesPerPack = this.product.tags.find(tag => tag.toLowerCase().includes('pack_items|'))

      return bottlesPerPack ? parseInt(bottlesPerPack.split('|').pop()) : null
    },

    quantity () {
      return this.bottlesPerCase ? this.bottlesPerCase : 1
    },

    finalBottlePrice () {
      return this.isExcludedFromMemberPrice
        ? this.promotionalPrice
          ? this.bottlesPerPack
            ? this.promotionalPrice / this.bottlesPerPack
            : this.promotionalPrice
          : this.product.price
        : this.bottlesPerPack
          ? (this.product.price / this.bottlesPerPack) * app.membershipDiscount
          : this.product.price * app.membershipDiscount
    },

    finalCostPerCaseOrPack () {
      if (this.isExcludedFromMemberPrice) {
        const productPrice = this.promotionalPrice ? this.promotionalPrice : this.product.price
        if (this.bottlesPerCase) {
          return productPrice * this.bottlesPerCase
        } else {
          return productPrice
        }
      } else if (this.bottlesPerCase) return this.product.price * this.bottlesPerCase * app.membershipDiscount

      else if (this.bottlesPerPack) return this.product.price * app.membershipDiscount

      else return null
    },

    finalCostPerCaseOrPackNonDiscounted () {
      if (this.bottlesPerCase) return this.product.price * this.bottlesPerCase

      else if (this.bottlesPerPack) return this.product.price

      else return null
    },

    caseOrPackLabel () {
      const label = this.product.tags.find(tag => tag.toLowerCase().includes('qty_'))

      return label ? label.split('_').pop() : null
    },

    currentVariantInCart () {
      const variantInCart = this.cart?.items?.find(item => {
        if (item.id === this.currentVariant.id) {
          return item
        }
        return null
      })

      return this.cart?.items?.length ? variantInCart : null
    },

    isCartLoading () {
      return app.isCartLoading
    },

    quantitySelectorLabel () {
      const currentProductQuantity = this.currentVariantInCart?.quantity
      const productType = this.caseOrPackLabel

      if (!currentProductQuantity) return ''

      switch (productType) {
        case 'pack':
          return currentProductQuantity > 1 ? 'Packs' : 'Pack'
        default:
          return currentProductQuantity > 1 ? 'Bottles' : 'Bottle'
      }
    },

    isLowStock () {
      return this.product.tags.some(tag => tag.toLowerCase().includes('low-stock'))
    },

    isCellarDoor () {
      return this.product.tags.some(tag => tag.toLowerCase().includes('cellar-door-only'))
    },

    isNotShoppable () {
      return this.product.tags.some(tag => tag.toLowerCase().includes('not-shoppable'))
    },

    isExcludedFromMemberPrice () {
      return this.product.tags.some((tag) => tag === 'dw-custom') && window?.membership_discounts
    },

    nonDiscountable () {
      return this.product.tags.some((tag) => tag === 'non-discountable') || (this.product.tags.some((tag) => tag === 'dw-custom') && !this.promotionalPrice)
    },

    currentSku () {
      return this.currentVariant?.sku || this.product?.variants?.[0]?.sku
    },

    purchasedWelcomeDwQuantities () {
      if (!this.isWelcomeDwComputed || !this.customerTagsComputed) return {}

      const quantities = {}
      const tags = this.customerTagsComputed.split(',')

      tags.forEach(tag => {
        const trimmedTag = tag.trim()
        if (trimmedTag.startsWith('welcome-purchase-')) {
          const match = trimmedTag.match(/welcome-purchase-.+-(.+?):(\d+)$/)
          if (match) {
            const sku = match[1]
            const qty = parseInt(match[2]) || 0
            quantities[sku] = (quantities[sku] || 0) + qty
          }
        }
      })

      return quantities
    },

    currentSkuPurchasedQuantity () {
      if (!this.isWelcomeDwComputed || !this.currentSku) return 0
      const rawPurchasedQuantity = this.purchasedWelcomeDwQuantities[this.currentSku] || 0
      return this.bottlesPerCase ? Math.ceil(rawPurchasedQuantity / this.bottlesPerCase) : rawPurchasedQuantity
    },

    currentSkuCartQuantity () {
      if (!this.isWelcomeDwComputed || !this.currentSku) return 0

      const cartItems = this.cart?.items || []
      const totalCartQuantity = cartItems
        .filter(item => item.sku === this.currentSku)
        .reduce((total, item) => total + item.quantity, 0)

      return this.bottlesPerCase ? Math.ceil(totalCartQuantity / this.bottlesPerCase) : totalCartQuantity
    },

    currentSelectedStacks () {
      if (!this.isWelcomeDwComputed) return this.quantity

      return this.bottlesPerCase ? Math.ceil(this.quantity / this.bottlesPerCase) : this.quantity
    },
    welcomeDwLimitExceeded () {
      if (!this.isWelcomeDwComputed || !this.welcomeDwLimitComputed) return false
      const totalStacks = this.currentSkuPurchasedQuantity + this.currentSkuCartQuantity + this.currentSelectedStacks
      return totalStacks > this.welcomeDwLimitComputed
    },

    isWelcomeDwUnavailable () {
      if (!this.isWelcomeDwComputed) return false
      const isNotLoggedIn = !window.liquid_is_logged_in
      return isNotLoggedIn || this.welcomeDwLimitExceeded
    }
  },

  created () {
    this.productOptions = getProductOptions(this.product)
    this.media = this.featuredMedia || this.product.media?.[0]
    // eslint-disable-next-line no-useless-escape
    this.parsedFileUrl = this.rawFileUrl.replace(/[^\/]+$/, '').substring(0, this.rawFileUrl.lastIndexOf('files')) + 'files/'
  },

  methods: {
    swatchUrl (colour) {
      return `${app.fileUrl}colour-${colour}.jpg`.replace(/\s+/g, '-')
    },

    async handleAddToCart () {
      if (window.dwRedirectBase) {
        window.location.href = `${window.dwRedirectBase.replace(/\/$/, '')}/products/${this.product.handle}`
        return
      }

      if (this.currentVariant && !(this.isWelcomeDwComputed && this.welcomeDwLimitExceeded)) {
        this.isSubmitting = true

        const properties = {}

        if (this.isExcludedFromMemberPrice && this.promotionalPrice) {
          properties._promotional_price = this.promotionalPrice
        }

        if (this.isWelcomeDwComputed) {
          properties._is_welcome_product = true
        }

        await app.addToCart(this.currentVariant.sku, this.product.handle, this.quantity, Object.keys(properties).length ? properties : null).then(response => {

        }).catch(error => {
          console.log('error', error)
          alert('Cannot reach the server at this time, please try again later.')
        })

        this.isSubmitting = false
      }
    },

    updateVariant () {
      const variant = this._findVariant()
      this._selectVariant(variant)
    },

    _findVariant () {
      return this.product.variants.find(variant => isEqual(variant.options, this.selectedOptions))
    },

    // Overridden in product-detail.js
    _selectVariant (variant) {
      this.currentVariant = variant || null
    },

    _initSelectedOptions () {
      if (this.currentVariant) {
        this.selectedOptions = [...this.currentVariant.options]
        this._selectVariant(this.currentVariant)
      } else {
        this.selectedOptions = this.product.options.map(() => '')
      }
    },

    updateQuantity (...args) {
      app.updateQuantity(...args)
    },
    minusQuantity (...args) {
      app.minusQuantity(...args)
    },
    addQuantity (...args) {
      app.addQuantity(...args)
    }
  }
}
